/**
 * The HOME-WIDE projection of a request — T2075 M4 (plan D8, the second
 * keep-set). What a `reception` or `admin` account's Requests tab receives from
 * `GET /api/home/requests`.
 *
 * ⛔ A SECOND KEEP-SET, NOT A WIDENING OF THE FIRST. `nurse-request-projection.ts`
 * is a `group` account's My requests and answers "who asked" with two words plus
 * the staff name; this one answers a receptionist reading the whole home, so it
 * carries the booking-time GROUP snapshot and the entering account's ROLE — and
 * it never carries `equipment` or the account's display name (S8). ⭐ board 2732
 * M2 (ADR-0008): `notes` crosses ONLY when the home's cross-group switch is ON
 * (`HOME_REQUEST_WIDENED_KEYS`, decided by the route); with it OFF this wire
 * carries no notes at all, even on the viewer's own rows. The two keep-sets are
 * declared apart so neither can move the other, exactly as the nurse and operator
 * types are.
 *
 * ⛔ PICK, NEVER OMIT. Every key is NAMED below; nothing is spread. A
 * `{...row}`-then-`delete` projection leaks every field added later by default;
 * this one drops them by default. The spec's `CANARY` fixture carries a
 * `CANARY-MUST-NOT-CROSS` token in `equipment`, `nurseDisplayName`, the ids the
 * row is joined on, and `notes` (which must cross only with the switch ON), and
 * sweeps the serialised wire for it.
 *
 * ⛔ `enteredByRole` IS THE ENTERING ACCOUNT'S ROLE, NEVER ITS NAME (plan D17,
 * R-8's R-3 half): a reception "book on behalf" writes no access event, the
 * request row IS the record, and this key is how the home-wide list reads it.
 * It is the LIVE role off `portal.nurses` (the join the list already makes),
 * not a snapshot — nothing in v13 snapshots the role.
 */
import type { RequestStatus, ReturnAtState, SchedulePattern, TimeMeaning } from '../shared/request-schema.js';
import type { PortalRole } from '../shared/roles.js';
import type { PortalRequestListItem } from './requests-repo.js';
import {
  isParseableInstant,
  pickRequestChangeView,
  REQUEST_CHANGE_VIEW_KEYS,
  type HomeFacingHistory,
  type RequestChangeView,
} from './nurse-request-projection.js';

/** The list row this projection reads: the portal's own list shape plus the
 *  entering account's live role, selected by `listHomeRequests` alone. */
export type HomeRequestRow = PortalRequestListItem & { enteredByRole: PortalRole };

export type HomeRequestListItem = {
  requestId: number;
  /** Omitted by the renderer when null — the `portal.requests.job_ref` rule. */
  jobRef: string | null;
  residentDisplayName: string;
  /**
   * ⭐ THE ROOM, AND IT IS ON THIS KEEP-SET WHERE IT IS NOT ON THE NURSE'S. The
   * booking-time snapshot (`room_at_booking`), because the home's own reception
   * reads the home's own roster: the same fact `GET /api/meta` already hands
   * every account of the home. It never crosses the kiosk seam (ADR-0002 DD9),
   * and this projection is not that seam.
   */
  roomAtBooking: string;
  destinationName: string;
  /** The three formatter inputs travel with `journeyDate`, for the reason the
   *  nurse keep-set's docblock gives: without `timeMeaning` every appointment
   *  reads under the neutral `Time`, without `schedulePattern` a repeating row
   *  has no day sentence, and `returnAt` is never rendered without `returnTrip`. */
  returnTrip: boolean;
  returnAt: string | null;
  /** ⭐ T2088 M2 (ADR-0006) — the fourth formatter input, and the one that says
   *  what a null `returnAt` MEANS: `'to-confirm'` is a return whose time Helen
   *  will settle, `'unset'` is everything else. A home-wide list that carried
   *  the null without the word would show reception a return job with no
   *  "Back" line and no way to tell it from a one-way one. Same minimisation
   *  ground as `returnAt`: a fact about a journey, no resident content. */
  returnAtState: ReturnAtState;
  journeyDate: string | null;
  /** ⛔ The derived collection (board 2188). NULL on every pre-v12 row and on
   *  every `collection` run, so the renderer null-guards. See the keep-set. */
  pickupAt: string | null;
  schedulePattern: SchedulePattern | null;
  timeMeaning: TimeMeaning | null;
  status: RequestStatus;
  /** The STAFF member's display name, or null — keyed on the id exactly as the
   *  nurse projection keys it, so a name that arrived without its id does not
   *  cross (defence in depth over the LEFT JOIN). */
  requestedBy: string | null;
  /** The booking-time group snapshot, or null (pre-v13 rows). */
  groupLabelAtBooking: string | null;
  enteredByRole: PortalRole;
  /** ⭐ board 2707 (M1, R-2) — when the request was made, as an ISO instant, or
   *  null when the row's value is not a string (the column is NOT NULL; the
   *  null is the projection refusing to throw, never a database state). The
   *  reason it is on this keep-set is at the keep-set. */
  createdAt: string | null;
  /** ⭐ board 2732 (M1, R-10) — WHETHER East Coast Minibus has seen the
   *  request, never when: false when never seen, seen before v16 added the
   *  column, or a value that is not a parseable string (the projection refusing
   *  to throw). The reason it is on this keep-set is at the keep-set. */
  seen: boolean;
  /** ⛔ board 2732 (M2, ADR-0008) — the request's free-text notes, PRESENT ONLY
   *  when the home's cross-group switch is ON and ABSENT (not empty) otherwise;
   *  see `HOME_REQUEST_WIDENED_KEYS`. */
  notes?: string;
  // ⭐ Board 2788 M3: the change, the ask and the history, declared ONCE with
  // the nurse keep-set (`RequestChangeView`), where each key owes its reason.
} & RequestChangeView;

/**
 * ⛔ THE ALWAYS-ON KEEP-SET, PINNED BY NAME in
 * `test/core/home-request-projection.spec.ts` with a canary for `equipment`,
 * `nurseDisplayName` and (switch OFF) `notes`. Derive the count from the list;
 * never write it in a sentence.
 */
export const HOME_REQUEST_KEEP_KEYS = [
  'requestId',
  'jobRef',
  'residentDisplayName',
  'roomAtBooking',
  'destinationName',
  'returnTrip',
  'returnAt',
  // ⭐ T2088 M2 (ADR-0006) — the word beside the instant; see the type above.
  'returnAtState',
  'journeyDate',
  /**
   * ⛔⛔ ADDED AT BOARD 2188 (Alex, 2026-09-18), AND THIS KEEP-SET IS A
   * MINIMISATION BOUNDARY, NOT A CONVENIENCE LIST. Its spec is named *"the
   * home-wide payload is EXACTLY the keep-set"* and a sibling arm asserts named
   * keys never cross, so widening it is a deliberate act that owes its reason
   * here rather than in a commit message.
   *
   * THE REASON: reception meets the bus at the door. The appointment tells them
   * when the resident is expected somewhere else; only the collection tells them
   * when to have that person ready in the hall. Without it the one fact their job
   * turns on is the one fact their screen withholds — and the list is ordered by
   * the appointment, so the order itself is unexplainable without it.
   *
   * ⚠️ WHAT DOES **NOT** COME WITH IT: `pickupOffsetMinutesUsed` stays off this
   * projection and off the nurse's. Reception needs the WHEN, never the home's
   * internal allowance, and the offset is an operational setting rather than a
   * fact about this journey.
   * ⚠️ It is NULL on every pre-v12 row and on every `collection` run (the
   * derivation applies only on the `appointment` arm), so every reader must
   * null-guard rather than assume.
   */
  'pickupAt',
  'schedulePattern',
  'timeMeaning',
  'status',
  'requestedBy',
  'groupLabelAtBooking',
  'enteredByRole',
  /**
   * ⛔ ADDED AT BOARD 2707 (M1, R-2), AND IT OWES ITS REASON HERE, as the
   * `pickupAt` entry above says every widening does.
   *
   * THE REASON: reception reading the whole home is asked "when did we ask for
   * this?" — a request sent late is the first thing to rule out when a bus is
   * not booked — and the card answers with a `Requested` row. It is a fact about
   * the REQUEST, not resident content: one instant, no person, no health data,
   * joinable to nothing outside this system, and the nurse keep-set carries the
   * same key on the same ground. ⚠️ `updatedAt` does NOT come with it: nothing
   * renders it, and a key with no reader is a widening for nothing.
   */
  'createdAt',
  /**
   * ⛔ ADDED AT BOARD 2707 (M4, R-5), AND IT OWES ITS REASON HERE.
   *
   * THE REASON: "has anyone at the office seen it?" is the question a home
   * rings East Coast Minibus to ask (George, board 2707), and the card answers
   * with the step line's `Seen` node and its waiting sentence. It is a fact about
   * the REQUEST: one bit, no resident content, joinable to nothing.
   * ⛔ WHETHER, NEVER WHEN: board 2707 shipped the minute; board 2732 (Alex,
   * 2026-10-02) removed the time so the portal does not read as monitoring the
   * office. The ops list the kiosk reads keeps the full instant.
   */
  'seen',
  /**
   * ⛔ ADDED AT BOARD 2788 (M3, R-2 to R-6), AND IT OWES ITS REASON HERE: reception
   * reading the whole home is who answers "did our change go through, and what
   * did East Coast Minibus say to our ask?". The mark, the ask and the history
   * are facts about the JOURNEY and the home's own ask, worded without a person
   * (`RequestChangeView` carries each key's minimisation line); the history is
   * read through the same scope predicate as this list (S19).
   */
  ...REQUEST_CHANGE_VIEW_KEYS,
] as const;

/**
 * ⛔ THE CAP ON THE LIST READ (plan M4 Step 1): the 500 NEWEST rows by journey
 * date, so what the cap truncates is the oldest PAST rows. Declared here, beside
 * the wire shape it bounds, and read by the repo's `LIMIT`, the screen's hint
 * and the specs — one constant, never a figure typed twice.
 */
export const HOME_REQUESTS_CAP = 500;

/**
 * ⛔⛔ THE WIDENED KEYS (board 2732 M2, ADR-0008; Alex's ruling, board note
 * #4743): what crosses ON TOP of the keep-set when, and only when, the home's
 * cross-group switch is ON. The switch is already the home's recorded consent to
 * reception and admin reading every group's requests; this widens that read to
 * the request's NOTES (most likely pick-up notes, in Alex's words) and nothing
 * else. `equipment` (Article 9 health data) and the entering account's display
 * name stay off in every case. The decision is the ROUTE's, from the switch it
 * already reads; this file only obeys the boolean it is handed.
 */
export const HOME_REQUEST_WIDENED_KEYS = ['notes'] as const;

/**
 * The home-wide view of one request. Takes the full row and returns the narrow
 * shape; the narrowing is the point. ⛔ `notesVisible` is REQUIRED (the T1749
 * rule: `tsc` refusing the old one-argument call is the mechanism) and is the
 * home's switch value as the route read it; never call this through
 * `rows.map(pickHomeRequest)`, which would hand it the array INDEX.
 */
export function pickHomeRequest(row: HomeRequestRow & HomeFacingHistory, notesVisible: boolean): HomeRequestListItem {
  const picked: HomeRequestListItem = {
    requestId: row.requestId,
    jobRef: row.jobRef,
    residentDisplayName: row.residentDisplayName,
    roomAtBooking: row.room,
    destinationName: row.destinationName,
    returnTrip: row.returnTrip,
    returnAt: row.returnAt,
    returnAtState: row.returnAtState,
    journeyDate: row.journeyDate,
    pickupAt: row.pickupAt,
    schedulePattern: row.schedulePattern,
    timeMeaning: row.timeMeaning,
    status: row.status,
    requestedBy: row.requestedByStaffId === null ? null : row.requestedByName,
    groupLabelAtBooking: row.groupLabelAtBooking,
    enteredByRole: row.enteredByRole,
    // ⛔ A non-string becomes null: a list row never throws.
    createdAt: typeof row.createdAt === 'string' ? row.createdAt : null,
    // ⛔ Whether, never when (board 2732): a non-string or unparseable value
    // (never written by the repo, which emits an ISO string or null) is false.
    seen: isParseableInstant(row.seenAt),
    // ⭐ Board 2788 M3: the change, the ask and the history, picked by name.
    ...pickRequestChangeView(row),
  };
  // ⛔ `=== true`, never truthiness: anything but the boolean the route read
  // (an index, a stray 1) keeps the notes off.
  if (notesVisible === true) picked.notes = row.notes;
  return picked;
}
